import type { FC } from 'react';
import React, { useEffect, useContext } from 'react';
import { styled } from '@compiled/react';
import { useQuery } from '@apollo/react-hooks';

import { token } from '@atlaskit/tokens';

import {
	EXTENSION_POINT_SPACE_BUTTONS_EXPERIENCE,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import { ContentToolsWrapper } from '@confluence/content-tools';
import { EditContentButtonWrapper } from '@confluence/edit-button';
import { SPAViewContext } from '@confluence/spa-view-context';
import { WatchDialogWrapper, WATCH_DIALOG_LOCATION } from '@confluence/watch-dialog';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog';
import {
	INVITE_TO_EDIT_KEY,
	PAGE_FAVOURITE_KEY,
	PAGE_UNFAVOURITE_KEY,
	WATCH_BUTTON_KEY,
	EDIT_BUTTON_PAGE_KEY,
} from '@confluence/web-item';
import {
	PAGE_BUTTONS,
	WebItemLocation,
	type WebItemLocationChildrenFnWithLoading,
} from '@confluence/web-item-location';
import { TeammatePresenceLoader } from '@confluence/experiment-teammate-presence';
import { WebItemButtonList } from '@confluence/web-item-list';
import { HEADER_ITEM_KEYS } from '@confluence/header-items-manager';
import { RendererContentState } from '@confluence/content-state';
import { CommentButtonWrapper } from '@confluence/comment-button';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { useBooleanFeatureFlag } from '@confluence/session-data';
import { CopyLinkButtonLoader } from '@confluence/share';

import { SpaceHeader } from './SpaceHeader';
import { SpaceFavourite } from './SpaceFavourite';
import { FavouriteSpaceQuery } from './FavouriteSpaceQuery.graphql';
// Exported only for testing
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SpaceHeaderPlaceholder = styled.div({
	minHeight: '72px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DisplayFlex = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledWebItemButtonList = styled(WebItemButtonList)<typeof WebItemButtonList>({
	verticalAlign: 'middle',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WebItemsAndSubstitutedWebItems = styled.div({
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.100', '8px'),
});

const NOT_ALLOWED_OVERVIEW_WEB_ITEMS = [
	PAGE_FAVOURITE_KEY,
	PAGE_UNFAVOURITE_KEY,
	WATCH_BUTTON_KEY,
	EDIT_BUTTON_PAGE_KEY,
];

export interface SpaceOverviewGroupedButtonsProps {
	addItem: Function;
	initItems: Function;
	spaceKey: string;
	contentId: string;
	versionOverride?: number | null;
	spaceName: string;
	spaceOverviewQueryLoading: boolean;
	isThemed?: boolean;
	spaceIconPath?: string;
}

export const SpaceOverviewGroupedButtons: FC<SpaceOverviewGroupedButtonsProps> = ({
	addItem,
	initItems,
	spaceKey,
	contentId,
	versionOverride,
	spaceName,
	spaceOverviewQueryLoading,
	isThemed,
	spaceIconPath,
}) => {
	const isPageHeaderFixed = useBooleanFeatureFlag('confluence.frontend.fixed-page-header');
	useEffect(
		() => {
			initItems && initItems();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const { isAnonymous } = useContext(SPAViewContext);

	const { isExternalCollaborator } = useIsExternalCollaborator();

	const { data: favouriteStatusData } = useQuery(FavouriteSpaceQuery, {
		variables: {
			spaceKey,
		},
	});

	if (!isPageHeaderFixed && spaceOverviewQueryLoading) {
		return <SpaceHeaderPlaceholder data-testid="SpaceHeaderPlaceholder" />;
	}

	const isFavourited = favouriteStatusData?.space?.currentUser?.isFavourited;
	const spaceId = favouriteStatusData?.space?.id;

	return (
		<SpaceHeader
			spaceKey={spaceKey}
			isThemed={isThemed}
			spaceIconPath={spaceIconPath}
			contentId={contentId}
		>
			<DisplayFlex>
				<TeammatePresenceLoader contentId={contentId} spaceKey={spaceKey} contentType="overview" />
				<RendererContentState contentId={contentId} isPagePreview={false} />
				<EditContentButtonWrapper contentId={contentId} />
				<CommentButtonWrapper key="comment-button" contentId={contentId} isFabricPage />
				<WatchDialogWrapper
					contentId={contentId}
					defaultTab={MANAGE_WATCHER_TAB_TYPES.SPACE}
					componentLocation={WATCH_DIALOG_LOCATION.SPACE_OVERVIEW}
				/>
				<ExperienceStart name={EXTENSION_POINT_SPACE_BUTTONS_EXPERIENCE} timeout={10000} />
				{!isExternalCollaborator && (
					<WebItemLocation
						key="page-web-items"
						tagName="span"
						contentId={contentId}
						location={PAGE_BUTTONS}
						notAllowedWebItems={NOT_ALLOWED_OVERVIEW_WEB_ITEMS}
						renderWhenLoading={process.env.REACT_SSR as any}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ display: 'flex' }}
					>
						{
							(({ webItems }) => {
								const foundShareWebItem = webItems.some(
									(item) => item.completeKey === INVITE_TO_EDIT_KEY,
								);

								const webItemsAfterSubstitution: typeof webItems = [];
								webItems.forEach((item) => {
									if (item.completeKey !== INVITE_TO_EDIT_KEY) {
										webItemsAfterSubstitution.push(item);
									} else if (spaceName) {
										// FIXME: such property modifications should not be performed on pre-existing items; a new array with copies should be created instead
										item.contentId = contentId;
										addItem(HEADER_ITEM_KEYS.SHARE_PAGE);
										if (foundShareWebItem) addItem(HEADER_ITEM_KEYS.COPY_LINK);
										webItemsAfterSubstitution.push(item);
									}
								});

								return (
									<>
										{foundShareWebItem && (
											<CopyLinkButtonLoader
												contentId={contentId}
												spaceKey={spaceKey}
												contentType="page"
												pageMode="view"
												source="spaceOverview"
											/>
										)}
										<WebItemsAndSubstitutedWebItems>
											<StyledWebItemButtonList
												pageId={contentId}
												webItems={webItemsAfterSubstitution}
												id="extracted-share-button"
											/>
											<ExperienceSuccess name={EXTENSION_POINT_SPACE_BUTTONS_EXPERIENCE} />
										</WebItemsAndSubstitutedWebItems>
									</>
								);
							}) as WebItemLocationChildrenFnWithLoading
						}
					</WebItemLocation>
				)}

				<ContentToolsWrapper
					spaceKey={spaceKey}
					contentId={contentId}
					version={versionOverride}
					isExternalCollaborator={isExternalCollaborator}
				/>
				{isAnonymous ? null : (
					<SpaceFavourite
						key="space-favourite"
						spaceKey={spaceKey}
						contentId={contentId}
						isFavourited={isFavourited}
						spaceId={spaceId}
						analytics={{
							source: 'spaceOverview',
							attributes: {
								spaceId,
								isGuest: isExternalCollaborator,
							},
						}}
					/>
				)}
			</DisplayFlex>
		</SpaceHeader>
	);
};
